import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import CTASection from "@/components/site/CTASection";
import SEO from "@/components/site/SEO";
import { PROJECTS } from "@/lib/site";

const TAGS = ["All", "New Build", "Extension", "Garage Conversion", "Roofing", "Loft", "Kitchen", "Bathroom"];

export default function Projects() {
  const [tag, setTag] = useState("All");
  const filtered = useMemo(
    () => (tag === "All" ? PROJECTS : PROJECTS.filter((p) => p.tag === tag)),
    [tag]
  );

  return (
    <>
      <SEO
        title="Projects — L F Building & Maintenance Ltd"
        description="Explore recent residential builds, extensions, roofing, lofts, kitchens and bathrooms across Essex and Suffolk."
        path="/projects"
      />

      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-20 md:pb-28 border-b border-white/10">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9 space-y-6">
            <span className="eyebrow text-brand">Projects &amp; Gallery</span>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl leading-[0.9]">
              Selected <span className="text-brand">work.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              A snapshot of recent residential projects across Essex and Suffolk. Every image below represents a fixed
              programme delivered — on brief, on budget.
            </p>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <div className="bg-white border-b border-black/10 sticky top-24 z-30" data-testid="projects-filter">
        <div className="container-lf py-4 flex gap-2 overflow-x-auto">
          {TAGS.map((t) => (
            <button
              key={t}
              onClick={() => setTag(t)}
              data-testid={`filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest border transition-all whitespace-nowrap ${
                tag === t
                  ? "bg-brand text-white border-brand"
                  : "bg-white text-brand-ink border-black/15 hover:border-brand hover:text-brand"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <section className="py-16 md:py-24 bg-white" data-testid="projects-grid">
        <div className="container-lf">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <Link
                key={`${p.slug}-${i}`}
                to={`/projects/${p.slug}`}
                data-testid={`project-card-${i}`}
                className="group border border-black/10 hover:border-brand transition-colors overflow-hidden bg-white"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-4 left-4 bg-brand text-white px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    {p.tag}
                  </div>
                  {p.gallery && p.gallery.length > 1 && (
                    <div className="absolute bottom-4 right-4 bg-white/95 text-brand-ink px-3 py-1 text-[11px] font-bold uppercase tracking-widest">
                      {p.gallery.length} photos
                    </div>
                  )}
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold uppercase text-xl leading-tight group-hover:text-brand transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-neutral-500 mt-1">{p.location}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-neutral-300 group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-neutral-500 py-20">No projects in this category yet.</p>
          )}
        </div>
      </section>

      <CTASection eyebrow="Next up on the site" title="Could be your project." />
    </>
  );
}
